import type { AxiosInstance } from 'axios'
import { apiLogger } from '@/utils/logger'
import { apiClient, handleApiError, handleApiResponse } from '../api'

/**
 * Normalize an unknown thrown value into an Error for structured logging.
 * Avoids `error as Error` casts at every catch site — a thrown non-Error
 * (string, object) is wrapped rather than mislabeled.
 */
function toLogError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error))
}

/**
 * Keys of a request body, for debug logging. Bodies are typed `unknown`
 * (arbitrary JSON that axios serializes), so guard before enumerating.
 */
function logDataKeys(data: unknown): string[] {
  return data && typeof data === 'object' ? Object.keys(data) : []
}

/**
 * Base service class providing common functionality for all service classes
 * Implements consistent error handling, logging, and API interaction patterns
 */
export abstract class BaseService {
  protected readonly apiClient: AxiosInstance
  protected readonly logger: typeof apiLogger
  protected abstract readonly baseURL: string

  constructor() {
    this.apiClient = apiClient
    this.logger = apiLogger
  }

  /**
   * Generic GET request with consistent error handling and response processing
   */
  protected async get<T>(
    endpoint: string,
    params?: Record<string, unknown>
  ): Promise<T> {
    try {
      this.logger.debug(`GET ${this.baseURL}${endpoint}`, { params })
      const response = await this.apiClient.get(`${this.baseURL}${endpoint}`, {
        params,
      })
      return handleApiResponse(response)
    } catch (error) {
      this.logger.error(
        `GET ${this.baseURL}${endpoint} failed`,
        toLogError(error)
      )
      throw handleApiError(error)
    }
  }

  /**
   * Generic POST request with consistent error handling and response processing
   */
  protected async post<T>(endpoint: string, data?: unknown): Promise<T> {
    try {
      this.logger.debug(`POST ${this.baseURL}${endpoint}`, {
        dataKeys: logDataKeys(data),
      })
      const response = await this.apiClient.post(
        `${this.baseURL}${endpoint}`,
        data
      )
      return handleApiResponse(response)
    } catch (error) {
      this.logger.error(
        `POST ${this.baseURL}${endpoint} failed`,
        toLogError(error)
      )
      throw handleApiError(error)
    }
  }

  /**
   * Generic PATCH request with consistent error handling and response processing
   */
  protected async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    try {
      this.logger.debug(`PATCH ${this.baseURL}${endpoint}`, {
        dataKeys: logDataKeys(data),
      })
      const response = await this.apiClient.patch(
        `${this.baseURL}${endpoint}`,
        data
      )
      return handleApiResponse(response)
    } catch (error) {
      this.logger.error(
        `PATCH ${this.baseURL}${endpoint} failed`,
        toLogError(error)
      )
      throw handleApiError(error)
    }
  }

  /**
   * Generic PUT request with consistent error handling and response processing
   */
  protected async put<T>(endpoint: string, data?: unknown): Promise<T> {
    try {
      this.logger.debug(`PUT ${this.baseURL}${endpoint}`, {
        dataKeys: logDataKeys(data),
      })
      const response = await this.apiClient.put(
        `${this.baseURL}${endpoint}`,
        data
      )
      return handleApiResponse(response)
    } catch (error) {
      this.logger.error(
        `PUT ${this.baseURL}${endpoint} failed`,
        toLogError(error)
      )
      throw handleApiError(error)
    }
  }

  /**
   * Generic DELETE request with consistent error handling
   */
  protected async delete(endpoint: string): Promise<void> {
    try {
      this.logger.debug(`DELETE ${this.baseURL}${endpoint}`)
      await this.apiClient.delete(`${this.baseURL}${endpoint}`)
    } catch (error) {
      this.logger.error(
        `DELETE ${this.baseURL}${endpoint} failed`,
        toLogError(error)
      )
      throw handleApiError(error)
    }
  }

  /**
   * Upload file with multipart/form-data
   */
  protected async upload<T>(
    endpoint: string,
    file: File,
    additionalData?: Record<string, string | Blob>
  ): Promise<T> {
    try {
      const formData = new FormData()
      formData.append('file', file)

      if (additionalData) {
        Object.entries(additionalData).forEach(([key, value]) => {
          formData.append(key, value)
        })
      }

      this.logger.debug(`UPLOAD ${this.baseURL}${endpoint}`, {
        fileName: file.name,
        fileSize: file.size,
        additionalDataKeys: Object.keys(additionalData || {}),
      })

      const response = await this.apiClient.post(
        `${this.baseURL}${endpoint}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )

      return handleApiResponse(response)
    } catch (error) {
      this.logger.error(
        `UPLOAD ${this.baseURL}${endpoint} failed`,
        toLogError(error)
      )
      throw handleApiError(error)
    }
  }

  /**
   * Get organization ID from auth store
   * This is a common pattern used across multiple services
   */
  protected getOrganizationId(): string {
    const authData = localStorage.getItem('auth-storage')
    if (!authData) throw new Error('No organization context')

    const parsed = JSON.parse(authData)
    const organizationId = parsed.state?.organization?.id

    if (!organizationId) throw new Error('No organization ID found')

    return organizationId
  }

  /**
   * Build query parameters for paginated requests
   */
  protected buildPaginationParams(params?: {
    page?: number
    perPage?: number
    status?: string
  }): Record<string, unknown> {
    const queryParams: Record<string, unknown> = {}

    if (params?.page) queryParams.page = params.page
    if (params?.perPage) queryParams.perPage = params.perPage
    if (params?.status) queryParams.status = params.status

    return queryParams
  }
}
